/**
 * Student Roster Table - Scalable teacher view with search, filter, sort, pagination
 *
 * Features:
 * - Search students by name or email
 * - Filter by status (All / Needs Support / Balanced / On Track)
 * - Sort by any column (click headers)
 * - Pagination (15 students per page)
 * - Teacher-focused UX for classes with 30+ students
 * - Tappy Brand Colors matching School/District Admin dashboards
 */

import React, { useState, useMemo } from 'react';
import { User, Search, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

// Official Tappy Brand Colors - Match School/District Admin Dashboards
const TappyBrandColors = {
  primary: {
    darkTeal: '#1A3E48',
    teal: '#297A92',
    mediumTeal: '#388899',
    brandBlue: '#76909F',
    brightTeal: '#42909E',
    aqua: '#4D99A5',
    lightAqua: '#5BA3AC',
    skyBlue: '#6DB4CD',
  },
  accent: {
    coral: '#EA6D37',
    gold: '#ECC962',
    brightGold: '#F8C341',
    tan: '#C59F65',
  },
  neutral: {
    darkSlate: '#3D4954',
    deepPurple: '#391339',
    mauve: '#8D7F88',
    mintGray: '#ABCACA',
    lightGray: '#D5DEDD',
    paleGray: '#ECF3F2',
    offWhite: '#F5F9FA',
    nearWhite: '#FCFDFE',
    charcoal: '#1F2937',
    mediumGray: '#6B7280',
    border: '#E5E7EB',
  },
  semantic: {
    success: '#6DB4CD',
    warning: '#ECC962',
    error: '#EA6D37',
    info: '#42909E',
    primary: '#297A92',
    secondary: '#76909F',
  }
};

const StudentRosterTable = ({ students, period = 'week' }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, needs_support, balanced, on_track
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const studentsPerPage = 15;

  // Filter and sort students
  const filteredAndSortedStudents = useMemo(() => {
    let filtered = students;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(student =>
        student.name.toLowerCase().includes(query) ||
        student.email.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(student => {
        if (statusFilter === 'needs_support') return student.independence_score < 40;
        if (statusFilter === 'balanced') return student.independence_score >= 40 && student.independence_score < 70;
        if (statusFilter === 'on_track') return student.independence_score >= 70;
        return true;
      });
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.key) {
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'mastery':
          aVal = a.mastery;
          bVal = b.mastery;
          break;
        case 'independence':
          aVal = a.independence_score;
          bVal = b.independence_score;
          break;
        case 'questions':
          aVal = a.questions_asked;
          bVal = b.questions_asked;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [students, searchQuery, statusFilter, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedStudents.length / studentsPerPage);
  const startIndex = (currentPage - 1) * studentsPerPage;
  const paginatedStudents = filteredAndSortedStudents.slice(startIndex, startIndex + studentsPerPage);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ?
      <ChevronUp size={14} className="inline ml-1" /> :
      <ChevronDown size={14} className="inline ml-1" />;
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <div style={{ height: '4px', width: '2rem', backgroundColor: TappyBrandColors.primary.teal, borderRadius: '9999px' }}></div>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: TappyBrandColors.neutral.charcoal }}>Student Roster</h2>
        <span style={{ fontSize: '0.875rem', color: TappyBrandColors.neutral.mediumGray, marginLeft: '0.5rem' }}>
          ({filteredAndSortedStudents.length} {filteredAndSortedStudents.length === 1 ? 'student' : 'students'})
        </span>
      </div>

      {/* Search and Filter Bar */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: '1rem',
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Search */}
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={18} style={{
              position: 'absolute',
              left: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: TappyBrandColors.neutral.mediumGray
            }} />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                paddingLeft: '2.5rem',
                paddingRight: '1rem',
                paddingTop: '0.5rem',
                paddingBottom: '0.5rem',
                border: `1px solid ${TappyBrandColors.neutral.border}`,
                borderRadius: '8px',
                fontSize: '0.875rem',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = TappyBrandColors.primary.teal}
              onBlur={(e) => e.target.style.borderColor = TappyBrandColors.neutral.border}
            />
          </div>

          {/* Status Filter */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => setStatusFilter('all')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                fontWeight: 500,
                fontSize: '0.875rem',
                transition: 'all 0.2s',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: statusFilter === 'all' ? TappyBrandColors.primary.teal : TappyBrandColors.neutral.paleGray,
                color: statusFilter === 'all' ? 'white' : TappyBrandColors.neutral.darkSlate
              }}
              onMouseEnter={(e) => {
                if (statusFilter !== 'all') e.target.style.backgroundColor = TappyBrandColors.neutral.lightGray;
              }}
              onMouseLeave={(e) => {
                if (statusFilter !== 'all') e.target.style.backgroundColor = TappyBrandColors.neutral.paleGray;
              }}
            >
              All
            </button>
            <button
              onClick={() => setStatusFilter('needs_support')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                fontWeight: 500,
                fontSize: '0.875rem',
                transition: 'all 0.2s',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: statusFilter === 'needs_support' ? TappyBrandColors.semantic.warning : TappyBrandColors.neutral.paleGray,
                color: statusFilter === 'needs_support' ? 'white' : TappyBrandColors.neutral.darkSlate
              }}
              onMouseEnter={(e) => {
                if (statusFilter !== 'needs_support') e.target.style.backgroundColor = TappyBrandColors.neutral.lightGray;
              }}
              onMouseLeave={(e) => {
                if (statusFilter !== 'needs_support') e.target.style.backgroundColor = TappyBrandColors.neutral.paleGray;
              }}
            >
              Needs Support
            </button>
            <button
              onClick={() => setStatusFilter('balanced')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                fontWeight: 500,
                fontSize: '0.875rem',
                transition: 'all 0.2s',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: statusFilter === 'balanced' ? TappyBrandColors.semantic.info : TappyBrandColors.neutral.paleGray,
                color: statusFilter === 'balanced' ? 'white' : TappyBrandColors.neutral.darkSlate
              }}
              onMouseEnter={(e) => {
                if (statusFilter !== 'balanced') e.target.style.backgroundColor = TappyBrandColors.neutral.lightGray;
              }}
              onMouseLeave={(e) => {
                if (statusFilter !== 'balanced') e.target.style.backgroundColor = TappyBrandColors.neutral.paleGray;
              }}
            >
              Balanced
            </button>
            <button
              onClick={() => setStatusFilter('on_track')}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                fontWeight: 500,
                fontSize: '0.875rem',
                transition: 'all 0.2s',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: statusFilter === 'on_track' ? TappyBrandColors.semantic.success : TappyBrandColors.neutral.paleGray,
                color: statusFilter === 'on_track' ? 'white' : TappyBrandColors.neutral.darkSlate
              }}
              onMouseEnter={(e) => {
                if (statusFilter !== 'on_track') e.target.style.backgroundColor = TappyBrandColors.neutral.lightGray;
              }}
              onMouseLeave={(e) => {
                if (statusFilter !== 'on_track') e.target.style.backgroundColor = TappyBrandColors.neutral.paleGray;
              }}
            >
              On Track
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {paginatedStudents.length === 0 ? (
          <div style={{
            padding: '3rem 1.5rem',
            textAlign: 'center',
            color: TappyBrandColors.neutral.mediumGray,
            fontSize: '0.875rem'
          }}>
            {searchQuery || statusFilter !== 'all'
              ? 'No students match your search or filter criteria.'
              : 'No students enrolled yet.'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      onClick={() => handleSort('name')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      Student <SortIcon columnKey="name" />
                    </th>
                    <th
                      onClick={() => handleSort('mastery')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      Mastery <SortIcon columnKey="mastery" />
                    </th>
                    <th
                      onClick={() => handleSort('independence')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      Independence <SortIcon columnKey="independence" />
                    </th>
                    <th
                      onClick={() => handleSort('questions')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      Questions <SortIcon columnKey="questions" />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Question Quality
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedStudents.map((student) => (
                    <tr
                      key={student.student_id}
                      onClick={() => setSelectedStudent(student)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div style={{
                              height: '2.5rem',
                              width: '2.5rem',
                              borderRadius: '9999px',
                              backgroundColor: `${TappyBrandColors.primary.teal}20`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <User size={20} style={{ color: TappyBrandColors.primary.teal }} />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{student.name}</div>
                            <div className="text-sm text-gray-500">{student.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-sm font-medium text-gray-900">{Math.round(student.mastery * 100)}%</div>
                          <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                            <div
                              className="h-2 rounded-full transition-all"
                              style={{
                                width: `${student.mastery * 100}%`,
                                backgroundColor: student.mastery >= 0.7 ? TappyBrandColors.semantic.success : student.mastery >= 0.5 ? TappyBrandColors.semantic.info : TappyBrandColors.semantic.warning
                              }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{student.independence_score}</div>
                        <div className="text-xs text-gray-500 capitalize">{student.independence_status}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {student.questions_asked}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-1 text-xs">
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: `${TappyBrandColors.semantic.success}20`,
                            color: TappyBrandColors.semantic.success,
                            borderRadius: '4px',
                            fontWeight: 500
                          }} title="Conceptual">
                            C: {student.question_quality.conceptual_pct}%
                          </span>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: `${TappyBrandColors.semantic.info}20`,
                            color: TappyBrandColors.semantic.info,
                            borderRadius: '4px',
                            fontWeight: 500
                          }} title="Factual">
                            F: {student.question_quality.factual_pct}%
                          </span>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: `${TappyBrandColors.semantic.warning}20`,
                            color: TappyBrandColors.accent.gold,
                            borderRadius: '4px',
                            fontWeight: 500
                          }} title="Procedural">
                            P: {student.question_quality.procedural_pct}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {student.independence_score >= 70 ? (
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            display: 'inline-flex',
                            fontSize: '0.75rem',
                            lineHeight: '1.25rem',
                            fontWeight: 600,
                            borderRadius: '9999px',
                            backgroundColor: `${TappyBrandColors.semantic.success}20`,
                            color: TappyBrandColors.semantic.success
                          }}>
                            On Track
                          </span>
                        ) : student.independence_score >= 40 ? (
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            display: 'inline-flex',
                            fontSize: '0.75rem',
                            lineHeight: '1.25rem',
                            fontWeight: 600,
                            borderRadius: '9999px',
                            backgroundColor: `${TappyBrandColors.semantic.info}20`,
                            color: TappyBrandColors.semantic.info
                          }}>
                            Balanced
                          </span>
                        ) : (
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            display: 'inline-flex',
                            fontSize: '0.75rem',
                            lineHeight: '1.25rem',
                            fontWeight: 600,
                            borderRadius: '9999px',
                            backgroundColor: `${TappyBrandColors.semantic.warning}20`,
                            color: TappyBrandColors.accent.gold
                          }}>
                            Needs Support
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                backgroundColor: TappyBrandColors.neutral.paleGray,
                padding: '0.75rem 1.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderTop: `1px solid ${TappyBrandColors.neutral.border}`
              }}>
                <div style={{ fontSize: '0.875rem', color: TappyBrandColors.neutral.darkSlate }}>
                  Showing {startIndex + 1} to {Math.min(startIndex + studentsPerPage, filteredAndSortedStudents.length)} of {filteredAndSortedStudents.length} students
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      border: currentPage === 1 ? 'none' : `1px solid ${TappyBrandColors.neutral.border}`,
                      backgroundColor: currentPage === 1 ? TappyBrandColors.neutral.paleGray : 'white',
                      color: currentPage === 1 ? TappyBrandColors.neutral.mediumGray : TappyBrandColors.neutral.darkSlate,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    <ChevronLeft size={16} /> Previous
                  </button>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    {[...Array(totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      // Show first, last, current, and adjacent pages
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '8px',
                              border: currentPage === page ? 'none' : `1px solid ${TappyBrandColors.neutral.border}`,
                              backgroundColor: currentPage === page ? TappyBrandColors.primary.teal : 'white',
                              color: currentPage === page ? 'white' : TappyBrandColors.neutral.darkSlate,
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              fontWeight: currentPage === page ? 600 : 400
                            }}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} style={{ padding: '0 0.5rem', color: TappyBrandColors.neutral.mediumGray }}>...</span>;
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      border: currentPage === totalPages ? 'none' : `1px solid ${TappyBrandColors.neutral.border}`,
                      backgroundColor: currentPage === totalPages ? TappyBrandColors.neutral.paleGray : 'white',
                      color: currentPage === totalPages ? TappyBrandColors.neutral.mediumGray : TappyBrandColors.neutral.darkSlate,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    Next <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}

            {/* Footer hint */}
            {totalPages <= 1 && (
              <div style={{
                backgroundColor: TappyBrandColors.neutral.paleGray,
                padding: '0.75rem 1.5rem',
                fontSize: '0.875rem',
                color: TappyBrandColors.neutral.mediumGray,
                textAlign: 'center',
                borderTop: `1px solid ${TappyBrandColors.neutral.border}`
              }}>
                Click any student to view detailed analytics
              </div>
            )}
          </>
        )}
      </div>

      {/* Student Detail Modal */}
      {selectedStudent && (
        <StudentDetailModal
          student={selectedStudent}
          period={period}
          onClose={() => setSelectedStudent(null)}
        />
      )}
    </div>
  );
};

// Student Detail Modal Component
const StudentDetailModal = ({ student, period, onClose }) => {
  const getPeriodLabel = () => {
    if (period === 'week') return 'This Week';
    if (period === 'month') return 'This Month';
    if (period === 'all') return 'All Time';
    return 'This Week';
  };

  const getStatusColor = (score) => {
    if (score >= 70) return TappyBrandColors.semantic.success;
    if (score >= 40) return TappyBrandColors.semantic.info;
    return TappyBrandColors.semantic.warning;
  };

  const getStatusLabel = (score) => {
    if (score >= 70) return 'On Track';
    if (score >= 40) return 'Balanced';
    return 'Needs Support';
  };

  const getRecommendations = () => {
    const recommendations = [];

    if (student.independence_score < 40) {
      recommendations.push({
        type: 'Independence',
        message: 'Student may be over-relying on AI assistance. Encourage more exploration and self-directed learning.',
        action: 'Reduce scaffolding gradually'
      });
    }

    if (student.question_quality.procedural_pct > 60 && student.questions_asked > 5) {
      recommendations.push({
        type: 'Question Quality',
        message: 'Most questions are procedural ("how to do"). Encourage deeper "why" questions.',
        action: 'Model conceptual questioning'
      });
    }

    if (student.mastery < 0.5 && student.questions_asked < 3) {
      recommendations.push({
        type: 'Engagement',
        message: 'Low mastery and few questions asked. Student may be disengaged or struggling silently.',
        action: 'Check in one-on-one'
      });
    }

    if (student.mastery > 0.7 && student.independence_score >= 70) {
      recommendations.push({
        type: 'Enrichment',
        message: 'Student is excelling and working independently. Ready for advanced challenges.',
        action: 'Provide extension activities'
      });
    }

    if (recommendations.length === 0) {
      recommendations.push({
        type: 'Progress',
        message: 'Student is making steady progress. Continue current approach.',
        action: 'Monitor regularly'
      });
    }

    return recommendations;
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: `2px solid ${TappyBrandColors.neutral.border}`,
          backgroundColor: TappyBrandColors.neutral.paleGray
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: TappyBrandColors.neutral.charcoal,
                marginBottom: '0.5rem'
              }}>
                {student.name}
              </h2>
              <p style={{ color: TappyBrandColors.neutral.mediumGray, fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                {student.email}
              </p>
              <p style={{ color: TappyBrandColors.primary.teal, fontSize: '0.75rem', fontWeight: 600 }}>
                📊 {getPeriodLabel()}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                padding: '0.5rem',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '1.5rem',
                color: TappyBrandColors.neutral.mediumGray,
                lineHeight: 1
              }}
            >
              ×
            </button>
          </div>

          {/* Status Badge */}
          <div style={{ marginTop: '1rem' }}>
            <span style={{
              padding: '0.5rem 1rem',
              borderRadius: '9999px',
              backgroundColor: `${getStatusColor(student.independence_score)}20`,
              color: getStatusColor(student.independence_score),
              fontSize: '0.875rem',
              fontWeight: 600
            }}>
              {getStatusLabel(student.independence_score)}
            </span>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          {/* Key Metrics Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            {/* Mastery */}
            <div style={{
              padding: '1rem',
              borderRadius: '12px',
              backgroundColor: TappyBrandColors.neutral.paleGray,
              border: `1px solid ${TappyBrandColors.neutral.border}`
            }}>
              <div style={{ fontSize: '0.75rem', color: TappyBrandColors.neutral.mediumGray, marginBottom: '0.5rem' }}>
                MASTERY
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: TappyBrandColors.primary.teal }}>
                {Math.round(student.mastery * 100)}%
              </div>
              <div style={{
                marginTop: '0.5rem',
                height: '4px',
                backgroundColor: TappyBrandColors.neutral.lightGray,
                borderRadius: '9999px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${student.mastery * 100}%`,
                  backgroundColor: getStatusColor(student.independence_score),
                  transition: 'width 0.3s'
                }} />
              </div>
            </div>

            {/* Independence */}
            <div style={{
              padding: '1rem',
              borderRadius: '12px',
              backgroundColor: TappyBrandColors.neutral.paleGray,
              border: `1px solid ${TappyBrandColors.neutral.border}`
            }}>
              <div style={{ fontSize: '0.75rem', color: TappyBrandColors.neutral.mediumGray, marginBottom: '0.5rem' }}>
                INDEPENDENCE
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: TappyBrandColors.primary.teal }}>
                {student.independence_score}
              </div>
              <div style={{ fontSize: '0.75rem', color: TappyBrandColors.neutral.mediumGray, textTransform: 'capitalize' }}>
                {student.independence_status}
              </div>
            </div>

            {/* Questions Asked */}
            <div style={{
              padding: '1rem',
              borderRadius: '12px',
              backgroundColor: TappyBrandColors.neutral.paleGray,
              border: `1px solid ${TappyBrandColors.neutral.border}`
            }}>
              <div style={{ fontSize: '0.75rem', color: TappyBrandColors.neutral.mediumGray, marginBottom: '0.5rem' }}>
                QUESTIONS ASKED
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: TappyBrandColors.primary.teal }}>
                {student.questions_asked}
              </div>
              <div style={{ fontSize: '0.75rem', color: TappyBrandColors.neutral.mediumGray }}>
                {student.topics_explored || 0} topics explored
              </div>
            </div>
          </div>

          {/* Question Quality Breakdown */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: TappyBrandColors.neutral.charcoal,
              marginBottom: '1rem'
            }}>
              Question Quality Breakdown
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {/* Conceptual */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.875rem', color: TappyBrandColors.neutral.darkSlate }}>
                    Conceptual ("Why?")
                  </span>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: TappyBrandColors.semantic.success }}>
                    {student.question_quality.conceptual_pct}%
                  </span>
                </div>
                <div style={{
                  height: '8px',
                  backgroundColor: TappyBrandColors.neutral.lightGray,
                  borderRadius: '9999px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${student.question_quality.conceptual_pct}%`,
                    backgroundColor: TappyBrandColors.semantic.success,
                    transition: 'width 0.3s'
                  }} />
                </div>
              </div>

              {/* Factual */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.875rem', color: TappyBrandColors.neutral.darkSlate }}>
                    Factual ("What?")
                  </span>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: TappyBrandColors.semantic.info }}>
                    {student.question_quality.factual_pct}%
                  </span>
                </div>
                <div style={{
                  height: '8px',
                  backgroundColor: TappyBrandColors.neutral.lightGray,
                  borderRadius: '9999px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${student.question_quality.factual_pct}%`,
                    backgroundColor: TappyBrandColors.semantic.info,
                    transition: 'width 0.3s'
                  }} />
                </div>
              </div>

              {/* Procedural */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.875rem', color: TappyBrandColors.neutral.darkSlate }}>
                    Procedural ("How to do?")
                  </span>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: TappyBrandColors.accent.gold }}>
                    {student.question_quality.procedural_pct}%
                  </span>
                </div>
                <div style={{
                  height: '8px',
                  backgroundColor: TappyBrandColors.neutral.lightGray,
                  borderRadius: '9999px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${student.question_quality.procedural_pct}%`,
                    backgroundColor: TappyBrandColors.accent.gold,
                    transition: 'width 0.3s'
                  }} />
                </div>
              </div>
            </div>
          </div>

          {/* Teaching Recommendations */}
          <div>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: TappyBrandColors.neutral.charcoal,
              marginBottom: '1rem'
            }}>
              Teaching Recommendations
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {getRecommendations().map((rec, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '1rem',
                    borderRadius: '12px',
                    backgroundColor: TappyBrandColors.neutral.paleGray,
                    border: `1px solid ${TappyBrandColors.neutral.border}`
                  }}
                >
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: TappyBrandColors.primary.teal,
                    marginBottom: '0.5rem',
                    textTransform: 'uppercase'
                  }}>
                    {rec.type}
                  </div>
                  <p style={{
                    fontSize: '0.875rem',
                    color: TappyBrandColors.neutral.darkSlate,
                    marginBottom: '0.5rem',
                    lineHeight: 1.5
                  }}>
                    {rec.message}
                  </p>
                  <div style={{
                    fontSize: '0.75rem',
                    color: TappyBrandColors.neutral.mediumGray,
                    fontStyle: 'italic'
                  }}>
                    → {rec.action}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: `1px solid ${TappyBrandColors.neutral.border}`,
          backgroundColor: TappyBrandColors.neutral.paleGray,
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: TappyBrandColors.primary.teal,
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentRosterTable;
